import React, { useMemo, useState } from "react";
import { TWebsiteBuilderSection } from "../../../../../nodes/section/websiteBuilderSection";
import { useTranslation } from "react-i18next";
import "./addSectionsView.scss";
import { websiteBuilderHeroTemplate } from "../../../../../nodes/section/templates/websiteBuilderHeroTemplate";
import { websiteBuilderCourseOverviewTemplate } from "../../../../../nodes/section/templates/websiteBuilderCourseOverviewTemplate";
import { websiteBuilderEventsTemplate } from "../../../../../nodes/section/templates/websiteBuilderEventsTemplate";
import { websiteBuilderMembershipPlansTemplate } from "../../../../../nodes/section/templates/websiteBuilderMembershipPlansTemplate";
import { websiteBuilderGalleryTemplate } from "../../../../../nodes/section/templates/websiteBuilderGalleryTemplate";
import { websiteBuilderContactTemplate } from "../../../../../nodes/section/templates/websiteBuilderContactTemplate";
import { websiteBuilderNewsletterTemplate } from "../../../../../nodes/section/templates/websiteBuilderNewsletterTemplate";
import { WebsiteBuilderToolbarMenuView } from "../../WebsiteBuilderToolbarMenuView";
import { DraggableNode } from "../../draggableNode/DraggableNode";
import { faMagnifyingGlass } from "@fortawesome/pro-regular-svg-icons";
import Input from "components/form/input/Input";
import { useAppSelector } from "hooks/redux";

interface IAddSectionsViewSection {
  title: string;
  src: string;
  item: TWebsiteBuilderSection;
}

export function AddSectionsView() {
  const { t } = useTranslation();

  const stockMedia = useAppSelector(store => store.websiteBuilderStore.editor.stockMedia);
  const iconAssets = useAppSelector(store => store.websiteBuilderStore.editor.iconAssets);

  const [searchValue, setSearchValue] = useState("");

  const sections: IAddSectionsViewSection[] = useMemo(
    () => [
      {
        title: t("Hero"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_hero_template.png",
        item: websiteBuilderHeroTemplate(stockMedia),
      },
      {
        title: t("Course overview"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_course_overview_template.png",
        item: websiteBuilderCourseOverviewTemplate(stockMedia),
      },
      {
        title: t("Events"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_events_template.png",
        item: websiteBuilderEventsTemplate(stockMedia),
      },
      {
        title: t("Membership plans"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_membership_plans_template.png",
        item: websiteBuilderMembershipPlansTemplate(iconAssets),
      },
      {
        title: t("Gallery"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_gallery_template.png",
        item: websiteBuilderGalleryTemplate(stockMedia),
      },
      {
        title: t("Contact"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_contact_template.png",
        item: websiteBuilderContactTemplate(stockMedia, iconAssets),
      },
      {
        title: t("Newsletter"),
        src: "/public/images/websiteBuilder/editor/sectionTemplates/website_builder_newsletter_template.png",
        item: websiteBuilderNewsletterTemplate(stockMedia),
      },
    ],
    [t, stockMedia, iconAssets],
  );

  const filteredSections = useMemo(() => {
    const normalizedSearchValue = searchValue.trim().toLocaleLowerCase();

    if (normalizedSearchValue === "") {
      return sections;
    } else {
      return sections.filter(section => section.title.toLocaleLowerCase().includes(normalizedSearchValue));
    }
  }, [searchValue, sections]);

  function renderSections() {
    return (
      <div className="website-builder-add-sections-view">
        <Input
          labelHidden
          value={searchValue}
          placeholder={t("Search sections...")}
          icon={faMagnifyingGlass}
          onChange={event => setSearchValue(event.target.value)}
        />
        {filteredSections.length > 0 ? (
          <div className="add-sections-view-templates">
            {filteredSections.map(section => (
              <div className="add-sections-view-template" key={section.title}>
                <h4>{section.title}</h4>
                <div className="add-sections-view-template-divider" />
                <DraggableNode item={section.item}>
                  <div className="add-sections-view-template-preview">
                    <img className="add-sections-view-template-section" src={section.src} alt={section.title} />
                  </div>
                </DraggableNode>
              </div>
            ))}
          </div>
        ) : (
          <div className="add-sections-view-templates-empty">{t("No sections found")}</div>
        )}
      </div>
    );
  }

  return (
    <WebsiteBuilderToolbarMenuView
      title={t("Add sections")}
      sections={[
        {
          content: renderSections(),
        },
      ]}
    />
  );
}
